import { BoxGeometry, ConeGeometry, Group, Mesh, MeshLambertMaterial, Scene, Vector3 } from 'three';
import { HALF_CITY, POPULATION } from '../../config/settings';
import { Updatable } from '../../core/types';
import { randFloat, randInt } from '../../utils/random';

interface Bird {
  readonly group: Group;
  readonly wingL: Mesh;
  readonly wingR: Mesh;
}

interface Flock {
  readonly group: Group;
  readonly birds: Bird[];
  readonly direction: Vector3;
  readonly speed: number;
  readonly phase: number;
}

/** Flocks of little birds drifting across the sky with flapping wings. */
export class BirdFlocks implements Updatable {
  private readonly flocks: Flock[] = [];
  private readonly bound = HALF_CITY + 120;
  private elapsed = 0;

  constructor(scene: Scene) {
    for (let i = 0; i < POPULATION.BIRD_FLOCKS; i++) {
      const flock = this.createFlock();
      scene.add(flock.group);
      this.flocks.push(flock);
    }
  }

  update(dt: number): void {
    this.elapsed += dt;
    for (const flock of this.flocks) {
      flock.group.position.addScaledVector(flock.direction, flock.speed * dt);
      this.wrap(flock.group.position);
      flock.group.rotation.y = Math.atan2(flock.direction.x, flock.direction.z);
      const flap = Math.sin(this.elapsed * 9 + flock.phase) * 0.7;
      for (const bird of flock.birds) {
        bird.wingL.rotation.z = flap;
        bird.wingR.rotation.z = -flap;
      }
    }
  }

  private wrap(p: Vector3): void {
    if (p.x > this.bound) p.x = -this.bound;
    else if (p.x < -this.bound) p.x = this.bound;
    if (p.z > this.bound) p.z = -this.bound;
    else if (p.z < -this.bound) p.z = this.bound;
  }

  private createFlock(): Flock {
    const group = new Group();
    const birds: Bird[] = [];
    const count = 5 + randInt(7);
    for (let i = 0; i < count; i++) {
      const bird = this.createBird();
      bird.group.position.set(randFloat(-22, 22), randFloat(-9, 9), randFloat(-22, 22));
      bird.group.scale.setScalar(randFloat(0.8, 1.4));
      group.add(bird.group);
      birds.push(bird);
    }
    group.position.set(randFloat(-HALF_CITY, HALF_CITY), randFloat(120, 210), randFloat(-HALF_CITY, HALF_CITY));
    return {
      group,
      birds,
      direction: new Vector3(randFloat(-1, 1), 0, randFloat(-1, 1)).normalize(),
      speed: randFloat(20, 34),
      phase: randFloat(0, 6),
    };
  }

  private createBird(): Bird {
    const group = new Group();
    const material = new MeshLambertMaterial({ color: 0x2b2b35 });
    const body = new Mesh(new ConeGeometry(0.35, 1.4, 5), material);
    body.rotation.x = -Math.PI / 2;
    group.add(body);
    const wingGeo = new BoxGeometry(2.2, 0.08, 0.8);
    const wingL = new Mesh(wingGeo, material);
    wingL.position.x = -1.1;
    const wingR = new Mesh(wingGeo, material);
    wingR.position.x = 1.1;
    group.add(wingL, wingR);
    return { group, wingL, wingR };
  }
}
